import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient.ts';
import { needsSignupCompletion } from './utils/routeAfterSignIn.ts';

/**
 * If the user *arrives* with a Supabase session ➜ /practice (or the finish
 * signup page when Google has authenticated them but they have no student
 * row yet). Otherwise render the child component.
 *
 * Only the session present on mount matters. Signing in from a child (the login
 * page) must not redirect from here, because the login page owns post-login
 * routing and needs to finish its role lookup first — non-students get handed
 * off to another app entirely.
 */
const AuthRedirect: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [needsComplete, setNeedsComplete] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setIsAuthed(false);
        setLoading(false);
        return;
      }
      setNeedsComplete(await needsSignupCompletion(session.user));
      setIsAuthed(true);
      setLoading(false);
    })();
  }, []);

  if (loading) return null;

  if (isAuthed && needsComplete) {
    return <Navigate to="/signup/complete" replace />;
  }

  return isAuthed ? <Navigate to="/practice" replace /> : children;
};

export default AuthRedirect;
